import Api from '@/modules/core/api/api.ts';
import { delay } from '@/modules/common/utils/promise.ts';
import { FileEndPoint } from '@/modules/common/enums/end-point.ts';
import ApiFile from '@/modules/document/models/file/api-file.ts';
import axios from 'axios';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import UpdatedFile from '@/modules/document/models/file/updated-file.ts';
import type DocumentFile from '@/modules/document/models/file/document-file.ts';
import type UploadFile from '@/modules/common/models/file/upload-file.ts';
import type { Page } from '@alphadoc/alphadoc-annotation';
import type {
  UploadTypes,
  UploadListTypes,
  RenameTypes,
  MoveTypes,
  AttachTypes,
} from '@/modules/document/models/file/file-api.type.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import type { ApiFileDto } from '@/modules/document/dto/api-file.dto.ts';
import { uploadFilesByName } from '@/modules/document/utils/upload-files-by-name.ts';
import { FILE_NAME_MAX_LENGTH } from '@/modules/common/consts/max-length.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import { useUserStore } from '@/modules/core/stores/user.store.ts';

export default class FileApi {
  static FILE_ATTRIBUTES = 'T';
  static FILE_GET_META_DELAY = 2000;
  static FILE_GET_META_MAX_ATTEMPTS = 15;
  private static pendingDocumentUploads = new WeakMap<UploadFile, { documentId: number; fileId: number }>();
  private static userUploadQueues = new Map<number | null, Promise<void>>();

  static async getUserFile(id: number, file: UploadFile, uploadedName: string, signal?: AbortSignal) {
    const response = await GatewayApi.get<ApiFileDto>(FileEndPoint.GetUserFiles, 'F_ID', id, signal);

    return new ApiFile(response, file, uploadedName);
  }

  static async resolveName(documentId: number, fileName: string, signal?: AbortSignal): Promise<string> {
    if (fileName.length <= FILE_NAME_MAX_LENGTH) return fileName;

    const response: { file_name?: unknown } | null = await Api.post(
      FileEndPoint.ResolveEdition,
      { doc_id: documentId, file_name: fileName },
      signal
    );

    if (
      typeof response?.file_name !== 'string' ||
      !response.file_name.trim() ||
      response.file_name.length > FILE_NAME_MAX_LENGTH
    ) {
      throw new Error('Invalid file_name in file edition resolve response.');
    }

    return response.file_name;
  }

  static async getEdition(
    tableId: TableId,
    parentId: number | null,
    name: string,
    signal?: AbortSignal
  ): Promise<number> {
    const { file_edition }: { file_edition: number } = await Api.post(
      FileEndPoint.GetEdition,
      {
        parent_table_id: tableId,
        parent_id: parentId,
        name,
      },
      signal
    );

    return file_edition;
  }

  static async upload({ tableId, parentId, file, comment }: UploadTypes, signal?: AbortSignal): Promise<number> {
    const originalName = file.name;
    if (tableId === TableId.Documents && originalName.length > FILE_NAME_MAX_LENGTH) {
      if (parentId === null) throw new Error('The document ID is not defined.');

      let pending = FileApi.pendingDocumentUploads.get(file);
      if (!pending || pending.documentId !== parentId) {
        const fileId = await FileApi.upload(
          { tableId: TableId.Users, parentId: useUserStore().required.id, file, comment },
          signal
        );
        pending = { documentId: parentId, fileId };
        FileApi.pendingDocumentUploads.set(file, pending);
      }

      await FileApi.attachToDocument(
        { fileId: pending.fileId, documentId: parentId, originalName, comment: comment || file.comment },
        signal
      );
      FileApi.pendingDocumentUploads.delete(file);
      return pending.fileId;
    }

    if (tableId === TableId.Users && originalName.length > FILE_NAME_MAX_LENGTH) {
      const pending = (FileApi.userUploadQueues.get(parentId) ?? Promise.resolve()).then(() =>
        FileApi.uploadContent({ tableId, parentId, file, comment }, signal)
      );
      const settled = pending.then(
        () => undefined,
        () => undefined
      );
      FileApi.userUploadQueues.set(parentId, settled);
      void settled.then(() => {
        if (FileApi.userUploadQueues.get(parentId) === settled) FileApi.userUploadQueues.delete(parentId);
      });
      return await pending;
    }

    return await FileApi.uploadContent({ tableId, parentId, file, comment }, signal);
  }

  private static async uploadContent(
    { tableId, parentId, file, comment }: UploadTypes,
    signal?: AbortSignal
  ): Promise<number> {
    const file_edition = await FileApi.getEdition(tableId, parentId, file.name, signal);

    if (file instanceof UpdatedFile) {
      await file.upload();
    }

    const { file_id } = await Api.post(
      FileEndPoint.Add,
      {
        parent_table_id: tableId,
        parent_id: parentId,
        file_name: file.name,
        file_size: file.size,
        file_content: file.base64,
        file_edition,
        comment: comment || (file as { comment?: string }).comment || undefined,
        attributes: FileApi.FILE_ATTRIBUTES,
      },
      signal
    );

    return file_id;
  }

  static async attachToDocument({ fileId, documentId, originalName, comment }: AttachTypes, signal?: AbortSignal) {
    const name = await FileApi.resolveName(documentId, originalName, signal);
    const revision = await FileApi.getEdition(TableId.Documents, documentId, name, signal);
    await FileApi.rename({ fileId, name, revision, comment: comment ?? undefined }, signal);
    return await FileApi.move({ fileId, documentId }, signal);
  }

  static async uploadList({ tableId, parentId, files, comment }: UploadListTypes, signal?: AbortSignal) {
    return await uploadFilesByName(files, (file) =>
      FileApi.upload(
        {
          tableId,
          parentId,
          file,
          comment: (file as { comment?: string }).comment || comment,
        },
        signal
      )
    );
  }

  static async review(file: DocumentFile, pages: Page[], annotations: Record<string, string>) {
    const edits = Object.entries(annotations).map(([page, svg]) => ({
      page_url: pages[Number(page) - 1].url,
      page_edits_svg: svg,
    }));

    return await Api.post(FileEndPoint.AddEdits, {
      parent_table_id: file.tableId,
      parent_id: file.parentId,
      file_edits: edits,
      file_name: file.name,
      comment: file.comment,
      attributes: FileApi.FILE_ATTRIBUTES,
    });
  }

  static async getPagesCount(id: number, hash: string, signal?: AbortSignal, attempt = 1): Promise<number> {
    try {
      const { count_pages } = await Api.post(FileEndPoint.GetMeta, { file_hash: hash }, signal);

      return count_pages;
    } catch (error) {
      if (
        axios.isCancel(error) ||
        (error as { error_code?: number }).error_code !== 1001 ||
        attempt >= FileApi.FILE_GET_META_MAX_ATTEMPTS
      ) {
        throw error;
      }

      await delay(FileApi.FILE_GET_META_DELAY);
      return await FileApi.getPagesCount(id, hash, signal, attempt + 1);
    }
  }

  static async addView(id: number, signal?: AbortSignal) {
    try {
      return await Api.post(FileEndPoint.AddView, { file_ids: [id] }, signal);
    } catch (error) {
      if (ErrorUtil.isUnexpected(error)) {
        console.warn('Error ignored', error);
      }
    }
  }

  static async rename({ fileId, name, revision, comment }: RenameTypes, signal?: AbortSignal) {
    return await Api.post(
      FileEndPoint.Edit,
      {
        file_id: fileId,
        file_name: name,
        file_edition: revision,
        comment: comment ?? null,
      },
      signal
    );
  }

  static async move({ fileId, documentId }: MoveTypes, signal?: AbortSignal) {
    return await Api.post(FileEndPoint.Move, { file_id: fileId, doc_id: documentId }, signal);
  }

  static async delete(fileId: number, signal?: AbortSignal) {
    return await Api.post(FileEndPoint.Delete, { file_id: fileId }, signal);
  }

  static async getEditConfig(id: number, timestamp: number, lang: string, events: Record<string, unknown>) {
    const response = await Api.post(FileEndPoint.GetEditConfig, {
      file_id: id,
      file_prev_key: timestamp,
    });

    return {
      ...response,
      config: {
        ...response.config,
        events,
        editorConfig: {
          ...response.config.editorConfig,
          lang,
        },
      },
    };
  }

  static async editNote(file: DocumentFile, comment: string) {
    return await Api.post(FileEndPoint.Edit, {
      file_id: file.fileId,
      file_name: file.name,
      file_edition: file.revision,
      comment,
    });
  }

  static async removeNote(file: DocumentFile) {
    return await Api.post(FileEndPoint.Edit, {
      file_id: file.fileId,
      file_name: file.name,
      file_edition: file.revision,
      comment: null,
    });
  }
}
